import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VerifyEmailRequest, EnrollmentDraft } from '../../../models/api-data-contracts';
import { EnrollmentService } from '../../../services/enrollment.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';

type step = 'select' | 'loading' | 'verify';
type error = 'timeout' | 'other' | 'change-email' | 'invalid-code';

@Component({
  selector: 'app-verify-identity',
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatIconModule],
  templateUrl: './verify-identity.component.html',
  styleUrl: './verify-identity.component.scss'
})
export class VerifyIdentityComponent implements OnInit, OnChanges, OnDestroy {
  @Input({required:true}) sessionToken: string;
  @Input({required:true}) maskedEmail: string;
  @Input({required:true}) memberData: EnrollmentDraft;
  @Input() infoMessage: string | null = null;
  @Input() otpExpiresAt: string | null = null; // ISO datetime when OTP expires
  @Input() isEmailVerified: boolean = false;
  @Output() verify = new EventEmitter<{ validationToken: string; isEnrollmentCompleted?: boolean }>();
  @Output() error = new EventEmitter<error>();
  @Output() otpExpiresAtUpdated = new EventEmitter<string | null>();
  step: step = 'verify';
  code: string = '';
  timeLeft: number = 0;
  interval: any;
  errorMessage: string | null = null;
  isTimerExpired: boolean = false;
  isResending: boolean = false; // Track resend state to prevent spam

 constructor(
    private enrollmentService: EnrollmentService,
  ) {}
  
  ngOnInit() {
    this.calculateAndStartTimer();
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }

  ngOnChanges() {
    // Restart timer if otpExpiresAt changes (e.g., after resend)
    if (this.otpExpiresAt) {
      this.calculateAndStartTimer();
    }
  }

  calculateAndStartTimer() {
    clearInterval(this.interval); // Clear any existing timer
    
    if (!this.otpExpiresAt) {
      // No expiration time means OTP has expired or wasn't sent
      this.timeLeft = 0;
      this.isTimerExpired = true;
      return;
    }

    // Calculate time remaining
    const expirationTime = new Date(this.otpExpiresAt).getTime();
    const currentTime = new Date().getTime();
    const remainingMs = expirationTime - currentTime;

    if (remainingMs <= 0) {
      // Already expired
      this.timeLeft = 0;
      this.isTimerExpired = true;
      // Don't set errorMessage - let the orange box handle the expired state
    } else {
      // Timer is active
      this.timeLeft = Math.floor(remainingMs / 1000);
      this.isTimerExpired = false;
      this.errorMessage = null;
      this.startTimer();
    }
  }

  resend(){
    if (this.isResending) {
      return; // Prevent multiple simultaneous requests
    }

    this.errorMessage = null; // Clear any error
    this.isResending = true;
    clearInterval(this.interval);
    
    // Resend verification email by updating draft with resendOtp flag
    this.enrollmentService.updateDraft(this.sessionToken, this.memberData, true).subscribe({
      next: (response) => {
        if (response.valid && response.data) {
          // Update OTP expiration time from response
          const newOtpExpiresAt = response.data.otpExpiresAt;
          this.otpExpiresAtUpdated.emit(newOtpExpiresAt);
          
          // Recalculate timer with new expiration
          this.otpExpiresAt = newOtpExpiresAt;
          this.calculateAndStartTimer();
          
          // Reset resending flag after a short delay to prevent spam
          setTimeout(() => {
            this.isResending = false;
          }, 2000);
        } else {
          this.errorMessage = 'Failed to resend code. Please try again.';
          this.error.emit('other');
          this.isResending = false;
        }
      },
      error: () => {
        this.errorMessage = 'Failed to resend code. Please try again.';
        this.error.emit('other');
        this.isResending = false;
      }
    });
  }
  
  startTimer() {
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        clearInterval(this.interval);
        this.isTimerExpired = true;
        // Don't set errorMessage - let the orange box handle the expired state
      }
    }, 1000);
  }
  
  formatTime(): string {
    const minutes = Math.floor(this.timeLeft / 60);
    const seconds = this.timeLeft % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  
  verifyCode() {
    this.errorMessage = null; // Clear previous error
    this.step = 'loading'; // Show loading spinner
    
    const request: VerifyEmailRequest = {
      sessionToken: this.sessionToken,
      otpToken: this.code
    };
    
    this.enrollmentService.verifyEmail(request).subscribe({
      next: (response) => {
        if (response.valid && response.data?.isEmailVerified) {
          this.verify.emit({
            validationToken: response.data.validationToken,
            isEnrollmentCompleted: response.data.isEnrollmentCompleted
          });
        } else {
          this.step = 'verify'; // Stay on verify step
          this.errorMessage = 'Invalid verification code. Please check and try again.';
          this.error.emit('invalid-code');
        }
      },
      error: (err) => {
        this.step = 'verify'; // Stay on verify step
        
        // Parse error message from response
        const errorMessage = (err?.error?.messages?.[0]?.message || '').toLowerCase();

        if (errorMessage.includes('invalid session') || errorMessage.includes('draft not found')) {
          this.error.emit('other');
          return;
        }

        this.errorMessage = 'Invalid verification code. Please check and try again.';
        this.error.emit('invalid-code');
      }
    });
  }

  requestNewCode() {
    if (this.isResending) {
      return; // Prevent spam clicking
    }
    this.errorMessage = null; // Clear error
    this.code = ''; // Clear input
    this.resend(); // Trigger resend
  }

  changeEmail() {
    //go back to form - emit a special event to go back to data step
    this.error.emit('change-email');
  }
}
